import { useState, useEffect, useRef, useCallback } from 'react';
import type { WidgetServerEvent, WidgetMessage } from '../types';

interface UseWebSocketOptions {
  tenant: string | null;
  channel: string;
  sessionToken: string | null;
  onEvent: (event: WidgetServerEvent) => void;
}

interface UseWebSocketReturn {
  /** Returns true if message was sent, false if WS is not connected. */
  sendMessage: (message: WidgetMessage) => boolean;
  connected: boolean;
  error: string | null;
  exhausted: boolean;
  reconnect: () => void;
}

const MAX_RECONNECT_DELAY = 30000;
const BASE_RECONNECT_DELAY = 1000;
const MAX_RETRIES = 15;

export function useWebSocket({ tenant, channel, sessionToken, onEvent }: UseWebSocketOptions): UseWebSocketReturn {
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exhausted, setExhausted] = useState(false);

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectAttemptRef = useRef(0);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(true);
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;

  const sessionTokenRef = useRef(sessionToken);
  sessionTokenRef.current = sessionToken;

  // Track the "active" socket instance so stale onclose handlers are ignored.
  // Each call to connect() increments this counter. The onclose handler
  // captures the value at creation time and compares it — if they differ,
  // the socket was intentionally replaced and the handler must not reconnect.
  const socketGenerationRef = useRef(0);

  const connect = useCallback(() => {
    if (!tenant || !sessionTokenRef.current) return;

    // Cancel any pending reconnect timer
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }

    // Close existing connection — its onclose will be a no-op due to generation check
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    // Increment generation — any prior socket's onclose will see a stale generation
    const generation = ++socketGenerationRef.current;

    let protocol: string;
    let host: string;

    // WebSocket URL: use VITE_API_URL or same origin (window.location).
    // CloudFront supports WebSocket natively — it proxies wss:// to the ALB
    // origin over ws:// (HTTP). No separate WS URL needed.
    const apiUrl = import.meta.env.VITE_API_URL;
    if (apiUrl) {
      const parsed = new URL(apiUrl);
      protocol = parsed.protocol === 'https:' ? 'wss:' : 'ws:';
      host = parsed.host;
    } else {
      protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      host = window.location.host;
    }

    const url = `${protocol}//${host}/ws/chat?tenant=${encodeURIComponent(tenant)}&channel=${encodeURIComponent(channel)}`;

    try {
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        if (!mountedRef.current || generation !== socketGenerationRef.current) return;
        ws.send(JSON.stringify({ type: 'auth', token: sessionTokenRef.current }));
        setConnected(true);
        setError(null);
        setExhausted(false);
        reconnectAttemptRef.current = 0;
      };

      ws.onmessage = (event) => {
        if (!mountedRef.current || generation !== socketGenerationRef.current) return;
        try {
          const data: WidgetServerEvent = JSON.parse(event.data);
          onEventRef.current(data);
        } catch {
          // Ignore malformed messages
        }
      };

      ws.onerror = () => {
        if (!mountedRef.current || generation !== socketGenerationRef.current) return;
        setError('WebSocket connection error');
      };

      ws.onclose = () => {
        // CRITICAL: If this socket was replaced by a newer connect() call,
        // generation will be stale — do NOT reconnect or touch state.
        if (!mountedRef.current || generation !== socketGenerationRef.current) return;

        setConnected(false);
        wsRef.current = null;

        if (reconnectAttemptRef.current >= MAX_RETRIES) {
          setExhausted(true);
          setError('Connection failed after maximum retry attempts');
          return;
        }

        const delay = Math.min(
          BASE_RECONNECT_DELAY * Math.pow(2, reconnectAttemptRef.current),
          MAX_RECONNECT_DELAY
        );
        reconnectAttemptRef.current++;

        reconnectTimerRef.current = setTimeout(() => {
          if (mountedRef.current) connect();
        }, delay);
      };
    } catch {
      setError('Failed to create WebSocket connection');
    }
  }, [tenant, channel]);

  useEffect(() => {
    mountedRef.current = true;
    if (sessionToken) {
      connect();
    }

    return () => {
      mountedRef.current = false;
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [connect, sessionToken]);

  const reconnect = useCallback(() => {
    reconnectAttemptRef.current = 0;
    setExhausted(false);
    setError(null);
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    connect();
  }, [connect]);

  const sendMessage = useCallback((message: WidgetMessage): boolean => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
      return true;
    }
    return false;
  }, []);

  return { sendMessage, connected, error, exhausted, reconnect };
}
